import { CsvIssue, UploadGroup } from '../types';

import { parseCsv, recordsFromCsv } from './csv';
import { groupRecords, validateRecords } from './validation';

export type FileParseOutcome =
  | { status: 'fatal'; fileError: string }
  | { status: 'headerErrors'; issues: CsvIssue[] }
  | { status: 'parsed'; issues: CsvIssue[]; groups: UploadGroup[] };

/**
 * Local (no network) phase of processing an uploaded CSV file: parse, map
 * template columns to records, and row-level validate. Pure — never touches
 * component state, so the fatal/header-error/no-rows file-level gates are
 * directly testable without rendering the hook.
 */
export const parseUploadFile = async (
  file: File
): Promise<FileParseOutcome> => {
  try {
    const text = await file.text();
    const parsed = parseCsv(text);
    if (parsed.headers.length === 0) {
      return { status: 'fatal', fileError: 'The file is empty.' };
    }

    const { records, issues: headerIssues } = recordsFromCsv(parsed);
    if (headerIssues.some((issue) => issue.severity === 'error')) {
      return { status: 'headerErrors', issues: headerIssues };
    }

    if (records.length === 0) {
      return {
        status: 'fatal',
        fileError: 'The file contains no data rows.',
      };
    }

    return {
      status: 'parsed',
      issues: [...headerIssues, ...validateRecords(records)],
      groups: groupRecords(records),
    };
  } catch (parseError) {
    return {
      status: 'fatal',
      fileError:
        parseError instanceof Error
          ? parseError.message
          : 'The file could not be read.',
    };
  }
};
